import { ArrowLeft, Download, Receipt } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { LoadingState } from '../../components/common/LoadingState';
import { getCourse, getTransaction } from '../../firebase/firestore';
import { useTenant } from '../../hooks/useTenant';
import { formatCurrency } from '../../utils/currency';
import { formatDate, formatDateTime } from '../../utils/date';

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatInvoiceNumber(transaction) {
  return transaction?.invoiceNumber || transaction?.receiptNumber || `INV-${String(transaction?.id || '').slice(0, 8).toUpperCase() || '00000000'}`;
}

function formatMethod(value) {
  const text = String(value || 'invoice').replace(/[_-]+/g, ' ').trim();
  return text ? `${text[0].toUpperCase()}${text.slice(1)}` : 'Invoice';
}

export function InvoiceViewPage() {
  const { invoiceId } = useParams();
  const { activeTenantId, activeTenant } = useTenant();
  const currency = activeTenant?.settings?.currency || 'KES';
  const invoiceRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [course, setCourse] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    if (!activeTenantId || !invoiceId) {
      setInvoice(null);
      setCourse(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const nextInvoice = await getTransaction(activeTenantId, invoiceId);
      setInvoice(nextInvoice);
      if (nextInvoice?.courseId) {
        setCourse(await getCourse(activeTenantId, nextInvoice.courseId));
      } else {
        setCourse(null);
      }
    } catch (error) {
      console.error('Unable to load invoice', error);
      setInvoice(null);
      setCourse(null);
    } finally {
      setLoading(false);
    }
  }, [activeTenantId, invoiceId]);

  useEffect(() => {
    load().catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [load]);

  const invoiceNumber = useMemo(() => formatInvoiceNumber(invoice), [invoice]);
  const issuedDate = toDate(invoice?.createdAt);
  const dueDate = toDate(invoice?.dueDate) || toDate(invoice?.paymentDate);
  const amount = Number(invoice?.amount || 0);
  const schoolName = activeTenant?.name || 'Academy';
  const invoiceTitle = invoice?.category === 'invoice' || invoice?.type === 'invoice' ? 'Invoice' : 'Fee Invoice';

  async function handleDownloadPdf() {
    if (!invoiceRef.current || downloading) return;

    setDownloading(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);

      const canvas = await html2canvas(invoiceRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        scrollX: 0,
        scrollY: -window.scrollY,
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const maxWidth = pageWidth - margin * 2;
      const maxHeight = pageHeight - margin * 2;
      const scale = Math.min(maxWidth / canvas.width, maxHeight / canvas.height);
      const imgWidth = canvas.width * scale;
      const imgHeight = canvas.height * scale;
      const x = (pageWidth - imgWidth) / 2;
      const y = (pageHeight - imgHeight) / 2;

      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, imgWidth, imgHeight);

      pdf.save(`${invoiceNumber}.pdf`);
    } catch (error) {
      console.error('Unable to download invoice PDF', error);
    } finally {
      setDownloading(false);
    }
  }

  if (loading) return <LoadingState label="Loading invoice..." />;

  if (!invoice) {
    return (
      <div className="incomeReceiptPage">
        <EmptyState
          title="Invoice not found."
          message="The invoice may have been removed or the link is incomplete."
          actionLabel="Back to Invoices"
          to="/dashboard/finance/invoices"
          icon={Receipt}
        />
      </div>
    );
  }

  return (
    <div className="incomeReceiptPage">
      <div className="incomeReceiptTopActions no-print">
        <Link className="viewBtn4" to="/dashboard/finance/invoices">
          <ArrowLeft size={16} /> Back
        </Link>
        <Button icon={Download} onClick={handleDownloadPdf} disabled={downloading}>
          {downloading ? 'Preparing PDF...' : 'Download PDF'}
        </Button>
      </div>

      <div className="incomeReceiptShell">
        <section className="incomeReceiptPaper" ref={invoiceRef}>
          <div className="incomeReceiptPrintArea">
            <div className="incomeReceiptHeader">
              <div className="incomeReceiptBrand">
                <div className="incomeReceiptMark" aria-hidden="true">
                  <Receipt size={20} />
                </div>
                <div>
                  <p className="incomeReceiptKicker">{schoolName}</p>
                  <h1>{invoiceTitle}</h1>
                  <p className="incomeReceiptSubtext">Official invoice sent to the student</p>
                </div>
              </div>

              <div className="incomeReceiptHeaderMeta">
                <div>
                  <span>Invoice No.</span>
                  <strong>{invoiceNumber}</strong>
                </div>
                <div>
                  <span>Due Date</span>
                  <strong>{dueDate ? formatDate(dueDate) : '—'}</strong>
                </div>
              </div>
            </div>

            <div className="incomeReceiptRule" />

            <div className="incomeReceiptAmountBar">
              <div>
                <span>Amount due</span>
                <strong>{formatCurrency(amount, currency)}</strong>
              </div>
              <div className="incomeReceiptBadge">{formatMethod(invoice.status || 'Sent')}</div>
            </div>

            <div className="incomeReceiptRule" />

            <div className="incomeReceiptMetaGrid">
              <div className="incomeReceiptMetaRow">
                <span>Student</span>
                <strong>{invoice.studentName || 'Student record unavailable'}</strong>
              </div>
              <div className="incomeReceiptMetaRow">
                <span>Course</span>
                <strong>{invoice.courseName || course?.name || '—'}</strong>
              </div>
              <div className="incomeReceiptMetaRow">
                <span>Email</span>
                <strong>{invoice.studentEmail || '—'}</strong>
              </div>
              <div className="incomeReceiptMetaRow">
                <span>Phone</span>
                <strong>{invoice.studentPhone || '—'}</strong>
              </div>
              <div className="incomeReceiptMetaRow">
                <span>Issued</span>
                <strong>{issuedDate ? formatDateTime(issuedDate) : '—'}</strong>
              </div>
              <div className="incomeReceiptMetaRow">
                <span>Reference</span>
                <strong>{invoice.reference || invoiceNumber}</strong>
              </div>
              <div className="incomeReceiptMetaRow">
                <span>Status</span>
                <strong>{invoice.status || 'Sent'}</strong>
              </div>
              <div className="incomeReceiptMetaRow">
                <span>Note</span>
                <strong>{invoice.note || '—'}</strong>
              </div>
            </div>

            <div className="incomeReceiptRule" />

            <div className="incomeReceiptSummaryLine">
              <div>
                <span>Billed to</span>
                <strong>{invoice.studentName || '—'}</strong>
              </div>
              <div>
                <span>Amount</span>
                <strong>{formatCurrency(amount, currency)}</strong>
              </div>
            </div>

            <div className="incomeReceiptFooter">
              <p>This invoice is computer-generated and does not require a signature.</p>
              <p>Keep this invoice for your records and payment follow-up.</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
